import React, { useState, useEffect } from "react";
import dynamic from 'next/dynamic';
import DtvText, { IDtvTextProps } from "../../video-lab/dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";
import { colorPalette, MOBILE_WIDTH } from "@directv-digital/dds.ui.dtv-theme";
import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import { IconCategory, AddOnIconCategory } from "./components";
import { IMyFreeConfig, IPackage } from "../chnlListingPage.props";
import { ZipcodeModuleProps } from "@/video-lab/zipcode-module";
import { IChnlLineupThemeUI } from "../server/themeConfig";

const IconBannerErrorPage = dynamic(() => import('./components/BannerErrorPage').then(mod => mod.IconBannerErrorPage));

export interface IconBannerProps {
  id: string;
  bannerChannels?: any;
  bannerCategories?: KeyValuePair[];
  bannerPackages?: IPackage[];
  isSearching: boolean;
  showMoreData?: any[];
  updateShowMoreCategories: (data: any) => void;
  openChannelModal: (channel: any) => void;
  zipCodeModule?: ZipcodeModuleProps;
  categoryFooterCtas?: {
    [key: string]: IDtvButtonProps[]; // key is the category id
  };
  localChnlBadgeTooltip?: IDtvTextProps["tooltip"];
  rsnChnlBadgeTooltip?: IDtvTextProps["tooltip"];
  internetChnlsPDFLink?: IDtvButtonProps;
  theming?: IChnlLineupThemeUI["categoriesTab"]["category"]
  noMatchPageMsg?: string;
  genreNationals?: any;
  myFreeConfig?: IMyFreeConfig;
}

const normalizeString = (str: string) => str?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[\s_&]+/g, "");

const IconBannerWAddOnChnls = (props: IconBannerProps) => {
  try {
    const {
      bannerChannels,
      bannerCategories,
      categoryFooterCtas,
      zipCodeModule: zipCodeModuleProps,
      localChnlBadgeTooltip,
      rsnChnlBadgeTooltip,
      theming,
      noMatchPageMsg,
      myFreeConfig
    } = props;

    const [showMoreKeys, setShowMoreKeys] = useState<any>();
    const [trackedCategories, setTrackedCategories] = useState<any[]>([]);

    const handleIconClick = () => {
      console.log("Icon clicked");
    }

    useEffect(() => {
      handleResize();
      ['load', 'resize'].forEach(evt => window?.addEventListener(evt, handleResize))
      return () => ['load', 'resize'].forEach(evt => window?.removeEventListener(evt, handleResize))
    }, []);

    const handleResize = () => {
      const iconCtnrs = document?.getElementsByClassName('chnlCtg-icons') || null;
      if (typeof document != "undefined" && iconCtnrs) {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < iconCtnrs?.length; i++) {
          const ctnr = iconCtnrs[i] as HTMLElement || null;
          if (ctnr) {
            calcShowMore(ctnr)
          }
        }
      }
    };

    useEffect(() => {
      const iconCtnrs = document?.getElementsByClassName('chnlCtg-icons') || null;
      if (iconCtnrs && bannerCategories?.length !== trackedCategories?.length) {
        if (iconCtnrs) {
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < iconCtnrs?.length; i++) {
            const ctnr = iconCtnrs[i] as HTMLElement || null;
            if (ctnr) {
              calcShowMore(ctnr)
            }
          }
        }
        setTrackedCategories(bannerCategories);
      }
    }, [bannerCategories]);

    const calcShowMore = iconCtnr => {
      // eslint-disable-next-line camelcase
      const ctg_key = iconCtnr?.getAttribute("id");
      const smElem: HTMLElement = iconCtnr.nextSibling;
      const { scrollHeight, clientHeight } = iconCtnr;
      if (iconCtnr && smElem && window?.innerWidth < MOBILE_WIDTH) {
        // eslint-disable-next-line camelcase
        if ((clientHeight < scrollHeight - 5 || scrollHeight > 478) || showMoreKeys?.[ctg_key]) {
          smElem.style.display = 'block';
          smElem.style.visibility = 'visible';
        } else {
          smElem.style.display = 'none';
          smElem.style.visibility = 'hidden';
        }
      } else {
        smElem.style.display = 'none';
        smElem.style.visibility = 'hidden';
      }
    };

    // eslint-disable-next-line camelcase
    const onShowMore = (ctg_key: string) => {
      console.log("onShowMore", ctg_key, "showMoreData", props?.showMoreData);
      // eslint-disable-next-line camelcase
      const nextSMVal = !(props?.showMoreData?.[ctg_key] || false);
      const nextSM = {
        ...props?.showMoreData,
        // eslint-disable-next-line camelcase
        [ctg_key]: nextSMVal
      }
      // eslint-disable-next-line camelcase
      const iconCtnr: HTMLElement = document.querySelector(`[id=${ctg_key}]`);
      iconCtnr.style.maxHeight = nextSMVal === true ? "5000px" : "475px";
      setShowMoreKeys(nextSM);
      props?.updateShowMoreCategories(nextSM);
    }

    // console.debug("Rendering Icon Banner... data: ", {
    //   bannerCategories,
    //   bannerChannels,
    //   bannerPackages: props?.bannerPackages
    // })

    return (
      <DtvBox id={`${props?.id}-channelsbanner`}
        sx={{
          padding: 0,
          margin: 0,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center"
      }}>
        <DtvBox
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            "& div:first-of-type": { marginTop: "0px" }
        }}>
          {Object?.keys(bannerChannels)?.length === 0 ? (
            <DtvText
              content={noMatchPageMsg}
              variant="body1"
              sx={{
                fontSize: {
 xs: "16px",
md: "20px"
},
                color: theming?.textColor,
                textAlign: "center",
                margin: "0px 16px 64px"
              }}
            />
          // eslint-disable-next-line array-callback-return
          ) : bannerCategories.map((ctg: any, index: any) => {
            const ctgKey = ctg?.key;
            const ctgValue = ctgKey === "MYFREE_DTV" ? ctg?.genreValue : ctg?.value;

            if (ctgKey && ctg?.key !== 'ADD_ON_CHANNELS') {
              if (bannerChannels[ctgKey]?.length > 0){
                const isLast = !(index < bannerCategories?.filter(ctg => ctg?.key !== 'ADD_ON_CHANNELS')?.length - 1);
                let packageData = props?.bannerPackages?.find(pkg => normalizeString(pkg?.name) === normalizeString(ctgKey));
                if(ctgKey === myFreeConfig?.myFreeCategory?.key) {
                  packageData = myFreeConfig?.packageData;
                }
                const footerCta = categoryFooterCtas?.[`${packageData?.name}`] || {};

                return (
                  <IconCategory
                    channels={bannerChannels[ctgKey]}
                    ctgIndex={index}
                    ctgKey={ctgKey}
                    ctgValue={ctgValue}
                    footerCta={footerCta}
                    genreNationals={props?.genreNationals}
                    handleIconClick={handleIconClick}
                    handleShowMoreClick={onShowMore}
                    isLast={isLast}
                    key={`banner-ctg-${ctgValue}`}
                    localChnlBadgeTooltip={localChnlBadgeTooltip}
                    packageData={packageData}
                    rsnChnlBadgeTooltip={rsnChnlBadgeTooltip}
                    showMoreKey={showMoreKeys?.[ctgKey]}
                    theming={theming}
                    zipCodeModuleProps={zipCodeModuleProps}
                  />
                );
              }
            } else if (ctg?.key === 'ADD_ON_CHANNELS') {
              const addOnChannels = bannerChannels.ADD_ON_CHANNELS;
              if(Object.keys(addOnChannels).some(key => addOnChannels[key].length > 0)) {
                return (
                  <DtvBox
                    id="ChnlListingPage-add-on-channels"
                    key={'ADD_ON_CHANNELS'}
                    sx={{
                      display: "flex",
justifyContent: "center",
flexWrap: "wrap",
                      backgroundColor: "#21528C !important",
                      padding: {
                        xs: "24px 14px",
                        sm: "54px 90px",
                        md: "91px 196px"
                      },
                      minWidth: {
                        xs: "calc(100vw - 28px)",
                        sm: "calc(100vw - 180px)",
                        md: "calc(100vw - 392px)",
                        lg: "100%"
                      },
                      order: 99
                    }}>
                      <DtvBox sx={{
 display: "flex",
flexDirection: "column",
width: "100%"
}}>
                        <DtvText
                          content={"ADD-ON CHANNELS"}
                          variant="subhead1medium"
                          sx={{
                            fontSize: {
 xs: "16px",
md: "24px"
},
                            color: colorPalette.white.main,
                            lineHeight: {
 xs: "20.8px",
sm: "18.2px",
md: "28.8px"
},
                            letterSpacing: {
 xs: "initial",
md: "-0.48px"
},
                            fontWeight: 400,
                            paddingBottom: {
 xs: "8px",
sm: "10px",
md: "12px"
}
                          }}
                        />
                        <DtvText
                          content={"Available for an additional cost."}
                          variant="body1"
                          sx={{
                            fontSize: {
 xs: "16px",
sm: "14px",
md: "20px"
},
                            color: colorPalette.white.main,
                            lineHeight: {
 xs: "20.8px",
sm: "18.2px",
md: "26px"
},
                            fontWeight: {
 xs: 400,
sm: 300,
md: 400
}
                          }}
                        />
                      </DtvBox>
                      {Object.keys(addOnChannels).length > 0 &&
                       // eslint-disable-next-line array-callback-return
                       Object.keys(addOnChannels).map((ctgKey: any) => {
                        if (ctgKey !== "" && addOnChannels[ctgKey]?.length > 0) {
                          const ctgValue = ctgKey;
                          return (
                            <AddOnIconCategory
                              channels={addOnChannels[ctgKey]}
                              ctgValue={ctgValue}
                              handleIconClick={handleIconClick}
                              key={`banner-addOnCtg-${ctgValue}`}
                            />
                          );
                        }
                      })}
                  </DtvBox>
                );
              }
                return (<></>)
            }
          })
          }
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    console.error("Icon Banner Error >>", error);
    try {
      return <IconBannerErrorPage
        chnlSrc="streamclup"
        internetChnlsPDFLink={props?.internetChnlsPDFLink || null}
      />
    } catch (error) {
      console.error("Icon Banner FATAL Error >>", error);
      return <></>;
    }
  }
};

export const MemoizedIconBanner = React.memo(IconBannerWAddOnChnls);
